/**
 * Poll quote.lastDelivery until email channel reaches a terminal status
 * for the *current* send attempt (not a prior delivery).
 */

import { getQuoteById } from "./quoteService.js";

const TERMINAL_EMAIL = new Set(["sent", "failed", "queue_failed", "skipped"]);

/**
 * @param {string} quoteId
 * @param {{
 *   intervalMs?: number,
 *   timeoutMs?: number,
 *   startedAt?: string|Date|null,
 *   campaignId?: string|null,
 * }} [options]
 * @returns {Promise<{
 *   success: boolean,
 *   status: string,
 *   error?: string,
 *   quote?: object,
 *   timedOut?: boolean,
 * }>}
 */
export async function waitForQuoteEmailDelivery(
  quoteId,
  {
    intervalMs = 2000,
    timeoutMs = 120000,
    startedAt = null,
    campaignId = null,
  } = {}
) {
  const deadline = Date.now() + timeoutMs;
  const startedMs = startedAt
    ? new Date(startedAt).getTime()
    : Date.now();

  while (Date.now() < deadline) {
    const response = await getQuoteById(quoteId);
    const quote = response?.data || response?.quote || response;
    if (!quote?.id && response?.success === false) {
      await new Promise((r) => setTimeout(r, intervalMs));
      continue;
    }

    const email = quote?.lastDelivery?.channels?.email;
    if (!email?.status || !TERMINAL_EMAIL.has(email.status)) {
      await new Promise((r) => setTimeout(r, intervalMs));
      continue;
    }

    // Require a timestamp from this attempt (or later)
    const emailAt = email?.at ? new Date(email.at).getTime() : 0;
    if (!emailAt || emailAt < startedMs - 1000) {
      await new Promise((r) => setTimeout(r, intervalMs));
      continue;
    }

    // Prefer matching campaign id when the API returned one
    if (
      campaignId &&
      email.campaignId &&
      String(email.campaignId) !== String(campaignId)
    ) {
      await new Promise((r) => setTimeout(r, intervalMs));
      continue;
    }

    if (email.status === "sent") {
      return { success: true, status: email.status, quote };
    }
    return {
      success: false,
      status: email.status,
      error: email.error || email.reason || "Quote email delivery failed",
      quote,
    };
  }

  return {
    success: false,
    status: "timeout",
    error: "Timed out waiting for quote email delivery",
    timedOut: true,
  };
}
